/**
 * Who registers new projects (JP, 30 Oct 2026): members may by default, and the organisation decides. Off, only
 * administrators (or a role that holds creating projects on the project register) register a new one; anyone else is
 * told to ask an administrator. A workbench keeps reopening the projects its person already belongs to either way.
 */

import { useState } from 'react';

import { routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { useMay } from '../permissions.ts';
import { ErrorNote, ListBlock, ListGroup, ListRow, Toggle } from './ui.tsx';

export function ProjectRegistrationPanel() {
  const org = useLoad(() => routes.organisationProfile(), []);
  const mayChange = useMay('organisation.settings', 'update');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  if (!org.data) return null;
  const on = org.data.projects?.membersRegister ?? true;
  const change = async (next: boolean) => {
    setError(undefined);
    setSaving(true);
    try {
      await routes.setProjectsMembersRegister(next);
      org.reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <ListGroup id="project-registration" title="New projects" description="Who may start a project the organisation does not have yet.">
      <ListRow
        title="Members may register new projects"
        description={
          on
            ? 'Anyone in the organisation can register a new project, from their workbench or here, and becomes its first owner.'
            : 'Only administrators register new projects. Anyone else is asked to have an administrator register it, or to be added to it. Projects people already belong to keep working.'
        }
        control={<Toggle label="Members may register new projects" checked={on} isDisabled={saving || !mayChange} onChange={(v) => void change(v)} />}
      />
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
